import { assertCategoriesAvailable } from '../../../services/resourceLifecycle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Button,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ArticleIcon from '@mui/icons-material/Article';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { FIRESTORE_BATCH_LIMIT } from '../../../utils/firestoreBatches';
import { db } from '../../../services/firebase';
import { profileName, isActiveProfile, normalizeProfileName, byProfileName, profileNameWinner } from '../../../utils/profileDisplay';
import { isTableUsable } from '../../../utils/tableLifecycle';
import { assertTableAcceptsWrites } from '../../../services/tableWrites';
import TagSelector from './TagSelector';
import { missingMandatoryAttributes, tagsToValueMap } from '../../../utils/mandatoryAttributes';
import { stableStringify } from '../../../utils/stableJson';

// A same-named profile's attributes changed between the review and the write.
const TARGET_CHANGED = 'import-target-changed';

const ImportDataProfilesDialog = ({ 
  open, 
  onClose, 
  companyId, 
  targetTableId, 
  targetTable 
}) => {
  const [loadingTables, setLoadingTables] = useState(false);
  const [tables, setTables] = useState([]);
  const [sourceTableId, setSourceTableId] = useState('');
  const [sourceTable, setSourceTable] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileIds, setSelectedProfileIds] = useState({});
  const [selectAllProfiles, setSelectAllProfiles] = useState(false);
  const [categoryIntersection, setCategoryIntersection] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [selectAllCategories, setSelectAllCategories] = useState(true);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState('');
  const [fixPlan, setFixPlan] = useState(null);  // [{profile, targetName, mode, tags, missing}] | null
  const [fixTags, setFixTags] = useState({});    // profile.id -> TagSelector selection array
  // A plan that overwrites same-named profiles, held while the user confirms it. An
  // update replaces the target's attributes wholesale, so the names are shown first.
  const [overwritePlan, setOverwritePlan] = useState(null);
  const [overwriteNote, setOverwriteNote] = useState('');
  // The source pick whose profile read is allowed to land. A getDocs cannot be
  // cancelled, so a read for a table the user has since moved off is checked
  // against this after it resolves and discarded — otherwise its profiles and
  // category intersection would sit under the selector's current choice and
  // be imported as that table's.
  const sourceRequestRef = useRef('');

  // Reset internal state when dialog opens/closes
  useEffect(() => {
    if (open) {
      sourceRequestRef.current = '';
      setSourceTableId('');
      setSourceTable(null);
      setProfiles([]);
      setSelectedProfileIds({});
      setSelectAllProfiles(false);
      setCategoryIntersection([]);
      setSelectedCategories({});
      setSelectAllCategories(true);
      setOverwriteExisting(false);
      setError('');
      setSuccessInfo('');
      setFixPlan(null);
      setFixTags({});
      setOverwritePlan(null);
      setOverwriteNote('');
      fetchTables();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchTables = async () => {
    if (!companyId) return;
    try {
      setLoadingTables(true);
      const tablesCollection = collection(db, `users/${companyId}/tables`);
      const snapshot = await getDocs(tablesCollection);
      const list = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(isTableUsable);
      setTables(list);
    } catch (e) {
      console.error('Error fetching tables for import:', e);
      setError('Failed to load tables');
    } finally {
      setLoadingTables(false);
    }
  };

  const handleSelectSourceTable = async (id) => {
    sourceRequestRef.current = id;
    setSourceTableId(id);
    setProfiles([]);
    setSelectedProfileIds({});
    setSelectAllProfiles(false);
    setCategoryIntersection([]);
    setSelectedCategories({});
    setSelectAllCategories(true);
    setFixPlan(null);
    setFixTags({});

    if (!id) {
      setSourceTable(null);
      setLoadingProfiles(false);
      return;
    }

    // The selector is built from `tables`, which fetchTables loaded in full
    // ({id, ...doc.data()}), so the picked table needs no second read.
    const sourceTbl = tables.find(t => t.id === id) || null;
    setSourceTable(sourceTbl);

    try {
      setLoadingProfiles(true);
      setError('');

      // Fetch profiles for source table (Active only — deleted profiles are not importable)
      const profilesRef = collection(db, `users/${companyId}/tables/${id}/data_profiles`);
      const profSnap = await getDocs(profilesRef);
      if (sourceRequestRef.current !== id) return;   // user picked another table meanwhile
      const profs = profSnap.docs.map(d => ({ ...d.data(), id: d.id })).filter(isActiveProfile).sort(byProfileName);
      setProfiles(profs);

      // Compute category intersection between source and target tables' metadata columns
      const srcMeta = (sourceTbl?.attributeColumns || []).map(c => c?.name).filter(Boolean);
      const tgtMeta = (targetTable?.attributeColumns || []).map(c => c?.name).filter(Boolean);
      const intersection = srcMeta.filter(name => tgtMeta.includes(name));
      setCategoryIntersection(intersection);
      const defaultSelected = {};
      intersection.forEach(name => { defaultSelected[name] = true; });
      setSelectedCategories(defaultSelected);
    } catch (e) {
      if (sourceRequestRef.current !== id) return;
      console.error('Error preparing import:', e);
      setError('Failed to load profiles');
    } finally {
      if (sourceRequestRef.current === id) setLoadingProfiles(false);
    }
  };

  const toggleSelectAllProfiles = () => {
    const newVal = !selectAllProfiles;
    setSelectAllProfiles(newVal);
    if (newVal) {
      const all = {};
      profiles.forEach(p => { all[p.id] = true; });
      setSelectedProfileIds(all);
    } else {
      setSelectedProfileIds({});
    }
  };

  const toggleProfile = (id) => {
    setSelectedProfileIds(prev => {
      const next = { ...prev, [id]: !prev[id] };
      const totalSelected = Object.values(next).filter(Boolean).length;
      setSelectAllProfiles(totalSelected === profiles.length && profiles.length > 0);
      return next;
    });
  };

  const toggleSelectAllCategories = () => {
    const newVal = !selectAllCategories;
    setSelectAllCategories(newVal);
    const next = {};
    categoryIntersection.forEach(name => { next[name] = newVal; });
    setSelectedCategories(next);
  };

  const toggleCategory = (name) => {
    setSelectedCategories(prev => {
      const next = { ...prev, [name]: !prev[name] };
      const totalSelected = Object.values(next).filter(Boolean).length;
      setSelectAllCategories(totalSelected === categoryIntersection.length && categoryIntersection.length > 0);
      return next;
    });
  };

  const selectedProfileList = useMemo(() => {
    const ids = new Set(Object.entries(selectedProfileIds).filter(([, v]) => v).map(([k]) => k));
    return profiles.filter(p => ids.has(p.id));
  }, [selectedProfileIds, profiles]);

  const selectedCategoriesList = useMemo(() => {
    return categoryIntersection.filter(name => selectedCategories[name]);
  }, [categoryIntersection, selectedCategories]);

  const prepareImport = async () => {
    if (!companyId || !targetTableId) return;
    if (!sourceTableId || selectedProfileList.length === 0) {
      setError('Select a source table and at least one profile');
      return;
    }

    try {
      setImporting(true);
      setError('');
      setSuccessInfo('');

      const targetProfilesRef = collection(db, `users/${companyId}/tables/${targetTableId}/data_profiles`);
      // Target-table Active profiles for the name lookup.
      const tgtSnap = await getDocs(targetProfilesRef);
      const targetProfiles = tgtSnap.docs.map(d => ({ ...d.data(), id: d.id })).filter(isActiveProfile);

      const plan = selectedProfileList.map(profile => {
        const filteredDefaultTags = {};
        selectedCategoriesList.forEach(cat => {
          if (profile.default_tags && Object.prototype.hasOwnProperty.call(profile.default_tags, cat)) {
            filteredDefaultTags[cat] = profile.default_tags[cat];
          }
        });
        const targetName = profileName(profile);
        const existing = targetProfiles.find(
          p => normalizeProfileName(profileName(p)) === normalizeProfileName(targetName)
        );
        const mode = existing ? (overwriteExisting ? 'update' : 'skip') : 'create';
        return {
          profile,
          targetName,
          mode,
          existingId: existing?.id || null,
          // The target as reviewed — its name and attributes; the write refuses if
          // either has changed by then, or if the profile has been deleted.
          existingName: existing ? profileName(existing) : null,
          existingTags: existing?.default_tags || null,
          tags: filteredDefaultTags,
          missing: mode === 'skip'
            ? []
            : missingMandatoryAttributes(targetTable?.attributeColumns || [], filteredDefaultTags),
        };
      });

      if (plan.some(entry => entry.missing.length > 0)) {
        setFixTags({});
        setFixPlan(plan);
        setImporting(false);
        return;
      }
      await reviewThenExecute(plan);
    } catch (e) {
      console.error('Error importing profiles:', e);
      setError('Failed to import one or more profiles');
    } finally {
      setImporting(false);
    }
  };

  // An update replaces a same-named profile's attributes with the imported ones, so
  // a plan holding any is shown for confirmation first; a plan that only creates
  // profiles is written straight away.
  const reviewThenExecute = async (plan) => {
    if (plan.some(entry => entry.mode === 'update')) {
      setOverwritePlan(plan);
      return;
    }
    await executeImport(plan);
  };

  const executeImport = async (plan) => {
    try {
      setImporting(true);
      setError('');
      setSuccessInfo('');
      const targetProfilesRef = collection(db, `users/${companyId}/tables/${targetTableId}/data_profiles`);
      const skipped = [];
      const created = []; // { name, ref } — settled against the table after the writes
      const updates = []; // { ref, entry, mergedTags } — same-named profiles to overwrite
      const categoryIds = new Set();
      const writes = []; // (transaction) => void, one per profile to write
      plan.forEach((entry) => {
        const mergedTags = { ...entry.tags, ...tagsToValueMap(fixTags[entry.profile.id]) };
        if (entry.mode === 'skip') {
          skipped.push(entry.targetName);
          return;
        }
        Object.keys(mergedTags).forEach((categoryId) => categoryIds.add(categoryId));
        if (entry.mode === 'update') {
          // Refresh tags only; the existing profile's learning/stats are untouched.
          const existingRef = doc(db, `users/${companyId}/tables/${targetTableId}/data_profiles/${entry.existingId}`);
          updates.push({ ref: existingRef, entry, mergedTags });
          writes.push((transaction) => transaction.update(existingRef, { default_tags: mergedTags }));
          return;
        }
        // No existing profile with this name — create afresh (auto-ID; no `id` field).
        const ref = doc(targetProfilesRef);
        writes.push((transaction) => transaction.set(ref, {
          name: entry.targetName,
          status: 'Active',
          default_tags: mergedTags,
          created_at: serverTimestamp(),
          file_stats: {
            total_files: 0,
            status_completed: 0,
            status_needs_attention: 0,
            status_processing: 0,
            last_updated: serverTimestamp()
          }
        }));
        created.push({ name: entry.targetName, ref });
      });
      if (writes.length > FIRESTORE_BATCH_LIMIT) {
        setError(`Import at most ${FIRESTORE_BATCH_LIMIT} profiles at a time.`);
        return;
      }
      // ONE atomic transaction: either every profile lands or none does, so a
      // failed import can be retried from the same plan without creating a
      // second copy of the ones that had already succeeded. The target table is
      // read inside it: one deleted from another tab while this dialog was open
      // must not have profiles recreated beneath it. So is every profile about
      // to be overwritten: attributes that changed since the user reviewed the
      // list are not overwritten on the strength of that review. Reads first.
      await runTransaction(db, async (transaction) => {
        await assertTableAcceptsWrites(transaction, companyId, targetTableId);
        for (const { ref, entry } of updates) {
          const snapshot = await transaction.get(ref);
          const current = snapshot.exists() ? snapshot.data() : null;
          const stillTheReviewedProfile = current !== null
            && isActiveProfile(current)
            && normalizeProfileName(profileName(current)) === normalizeProfileName(entry.existingName)
            && stableStringify(current.default_tags || {}) === stableStringify(entry.existingTags || {});
          if (!stillTheReviewedProfile) {
            const changed = new Error(`${entry.targetName} changed while you were reviewing.`);
            changed.code = TARGET_CHANGED;
            throw changed;
          }
        }
        await assertCategoriesAvailable(transaction, companyId, categoryIds);
        writes.forEach((write) => write(transaction));
      });

      // The creates were not atomic with the name lookup: another session may
      // have saved one of these names in the same instant. Read the table back
      // and settle each name with profileNameWinner; a losing create is
      // hard-deleted (nothing points at it yet) and reported as skipped.
      if (created.length > 0) {
        const after = await getDocs(targetProfilesRef);
        const profilesAfter = after.docs.map(d => ({ id: d.id, ...d.data() }));
        await Promise.all(created.map(async ({ name, ref }) => {
          const winner = profileNameWinner(profilesAfter, name);
          if (winner && winner.id !== ref.id) {
            await deleteDoc(ref);
            skipped.push(name);
          }
        }));
      }

      const importedCount = plan.length - skipped.length;
      if (skipped.length > 0) {
        setSuccessInfo(
          `Imported ${importedCount} profile(s). `
          + `Skipped ${skipped.length} existing profile(s): ${skipped.join(', ')}.`
        );
        setFixPlan(null);
        setFixTags({});
        setOverwritePlan(null);
        setImporting(false);
        return;
      }
      onClose({ success: true, importedCount });
    } catch (e) {
      if (e.code === TARGET_CHANGED) {
        // Nothing was written. Plan again from what the target table holds now
        // and show the confirmation again, saying why.
        setOverwritePlan(null);
        setOverwriteNote(`${e.message} The list below has been refreshed — check it again.`);
        await prepareImport();
        return;
      }
      console.error('Error importing profiles:', e);
      setError(`Nothing was imported: ${e.message || 'the write failed'}. Try again.`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={importing ? undefined : () => onClose(null)} maxWidth="md" fullWidth>
      <DialogTitle>
        Import Data Profiles
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {successInfo && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessInfo('')}>{successInfo}</Alert>
        )}

        {overwritePlan ? (
          <Box>
            {overwriteNote && <Alert severity="warning" sx={{ mb: 2 }}>{overwriteNote}</Alert>}
            <Typography variant="body2" sx={{ mb: 1 }}>
              The following profiles already exist in this table and will be overwritten.
              Each one's attributes are replaced by the imported values.
            </Typography>
            <List dense sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {overwritePlan.filter(entry => entry.mode === 'update').map(entry => (
                <ListItem key={entry.profile.id}>
                  <ListItemIcon><ArticleIcon /></ListItemIcon>
                  <ListItemText primary={entry.targetName} />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : fixPlan ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This table requires values for attributes the selected profiles do not have.
              Set them below to finish the import.
            </Alert>
            {fixPlan.filter(entry => entry.missing.length > 0).map(entry => (
              <Box key={entry.profile.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{entry.targetName}</Typography>
                <TagSelector
                  companyId={companyId}
                  tableMetadataColumns={(targetTable?.attributeColumns || [])
                    .filter(col => entry.missing.includes(col.name))}
                  selectedTags={fixTags[entry.profile.id] || []}
                  onTagsChange={(tags) => setFixTags(prev => ({ ...prev, [entry.profile.id]: tags }))}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Select Source Table</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Source table</InputLabel>
            <Select
              label="Source table"
              value={sourceTableId}
              onChange={(e) => handleSelectSourceTable(e.target.value)}
              disabled={loadingTables}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {/* Importing is for bringing another table's profiles here; a
                  profile of this table is duplicated with the sidebar's Copy. */}
              {tables.filter((t) => t.id !== targetTableId).map(t => (
                <MenuItem key={t.id} value={t.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FolderIcon sx={{ mr: 1 }} fontSize="small" />
                    <Typography variant="body2">{t.name || t.id}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Select Profiles</Typography>
            <Button
              onClick={toggleSelectAllProfiles}
              size="small"
              disabled={!sourceTableId || loadingProfiles || profiles.length === 0}
            >
              {selectAllProfiles ? 'Unselect All' : 'Select All'}
            </Button>
          </Stack>

          {loadingProfiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 240, overflow: 'auto', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {profiles.length === 0 && (
                <ListItem>
                  <ListItemText primary={sourceTableId ? 'No profiles found in source table' : 'Choose a source table to load profiles'} />
                </ListItem>
              )}
              {profiles.map(profile => (
                <ListItem key={profile.id} secondaryAction={null} disablePadding>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={!!selectedProfileIds[profile.id]}
                      tabIndex={-1}
                      disableRipple
                      onChange={() => toggleProfile(profile.id)}
                    />
                  </ListItemIcon>
                  <ListItemIcon>
                    <ArticleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2">{profileName(profile)}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{Object.keys(profile.default_tags || {}).length} attributes</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Select Attribute Categories to Import</Typography>
            <Button
              onClick={toggleSelectAllCategories}
              size="small"
              disabled={categoryIntersection.length === 0}
            >
              {selectAllCategories ? 'Unselect All' : 'Select All'}
            </Button>
          </Stack>

          {categoryIntersection.length === 0 ? (
            <Alert severity="info">No overlapping attribute categories between source and target tables. Importing will create profiles with no default attributes.</Alert>
          ) : (
            <List dense sx={{ maxHeight: 180, overflow: 'auto', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {categoryIntersection.map(name => (
                <ListItem key={name} disablePadding>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={!!selectedCategories[name]}
                      tabIndex={-1}
                      disableRipple
                      onChange={() => toggleCategory(name)}
                    />
                  </ListItemIcon>
                  <ListItemText primary={name} />
                </ListItem>
              ))}
            </List>
          )}

          <Box sx={{ mt: 2 }}>
            <FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={overwriteExisting}
                  onChange={(e) => setOverwriteExisting(e.target.checked)}
                />
                <Typography variant="body2">Update same-named profiles</Typography>
              </Box>
            </FormControl>
          </Box>
        </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {overwritePlan ? (
          <>
            <Button onClick={() => { setOverwritePlan(null); setOverwriteNote(''); }} disabled={importing}>Back</Button>
            <Button
              variant="contained"
              color="warning"
              disabled={importing}
              onClick={() => executeImport(overwritePlan)}
            >
              {importing ? 'Importing...' : 'Overwrite and import'}
            </Button>
          </>
        ) : fixPlan ? (
          <>
            <Button onClick={() => { setFixPlan(null); setFixTags({}); }} disabled={importing}>Back</Button>
            <Button
              variant="contained"
              disabled={importing || !fixPlan.every(entry =>
                missingMandatoryAttributes(
                  (targetTable?.attributeColumns || []).filter(col => entry.missing.includes(col.name)),
                  { ...entry.tags, ...tagsToValueMap(fixTags[entry.profile.id]) },
                ).length === 0)}
              onClick={() => reviewThenExecute(fixPlan)}
            >
              {importing ? 'Importing...' : `Import ${fixPlan.length}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => onClose(null)} disabled={importing}>Cancel</Button>
            <Button
              onClick={prepareImport}
              variant="contained"
              disabled={importing || !sourceTableId || selectedProfileList.length === 0}
            >
              {importing ? 'Importing...' : `Import ${selectedProfileList.length || ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDataProfilesDialog;
